import type { MRT_RowData, MRT_ColumnDef } from 'material-react-table'
import { useEffectOnce } from 'react-use'
import { useState } from 'react'
import { Box } from '@mui/material'
import _ from 'lodash'

import {
    SearchableSelect,
    type SearchableSelectOption,
    type SearchableSelectProps,
} from '../searchable-select'

export interface UseMultiValueColumnDefinitionProps<_RowData, ColumnData> {
    fieldName: string
    optionsData?: ColumnData[]
    renderChip?: (entry: ColumnData, index: number) => JSX.Element
    getOptions?: (data: ColumnData[]) => SearchableSelectOption[]
    onEdit?: (id: string, additions: string[], deletions: string[]) => void
    SearchableSelectProps?: Partial<
        SearchableSelectProps<SearchableSelectOption, true>
    >
}

/**
 * @param {string} fieldName           The name of the field.
 * @param {ColumnData[]} [optionsData]  The data used to generate options for editing and filtering.
 * @param {(entry: ColumnData, index: number) => JSX.Element} [renderChip]  Function to render each entry as a chip.
 * @param {(data: ColumnData[]) => SearchableSelectOption[]} [getOptions]  Function to generate options for the searchable select component.
 * @param {(id: string, additions: string[], deletions: string[]) => void} [onEdit]  Function to handle edits, receiving the row ID, additions, and deletions.
 * @param {Partial<SearchableSelectProps<SearchableSelectOption, true>>} [SearchableSelectProps]  Additional props for the SearchableSelect component.
 *
 * @returns {Partial<MRT_ColumnDef<RowData>>}  The column definition for a multi-value column.
 * 
 * @example
 * ```tsx
 * const getManufacturerOptions = (values: APIProductManufacturer[]) =>
 *     values.map((manufacturer) => ({
 *         label: manufacturer.name,
 *         value: manufacturer.id,
 *     }))
 * 
 * const manufacturerColumnDefs = useMultiValueColumnDef<
 *     APIProductModel,
 *     APIProductManufacturer
 * >({
 *     fieldName: 'Manufacturer',
 *     optionsData: manufacturers,
 *     getOptions: getManufacturerOptions,
 * })
 * ```
 */
export const useMultiValueColumnDef = <
    RowData extends MRT_RowData,
    ColumnData,
>({
    fieldName,
    optionsData,
    renderChip,
    getOptions,
    onEdit,
    SearchableSelectProps,
}: UseMultiValueColumnDefinitionProps<RowData, ColumnData>) => {
    const returnedOptions: Partial<MRT_ColumnDef<RowData>> = {}

    if (renderChip) {
        const Cell: MRT_ColumnDef<RowData>['Cell'] = ({ cell }) => {
            const entries = Array.from(cell.getValue<ColumnData[]>())
            return (
                <Box display="flex" alignItems="left" gap={0.6} flexWrap="wrap">
                    {entries.map((entry, index) => renderChip(entry, index))}
                </Box>
            )
        }

        returnedOptions.Cell = Cell
    }

    if (getOptions && optionsData && onEdit) {
        const options = getOptions(optionsData)
        const Edit: MRT_ColumnDef<RowData>['Edit'] = ({ cell, table }) => {
            const id = cell.row.getValue<string>('id')
            const originalValues = Array.from(cell.getValue<ColumnData[]>())
            const originalOptions = getOptions(originalValues)
            const [selectedOptions, setSelectedOptions] =
                useState(originalOptions)

            const onBlur = () => {
                const selectedIds = selectedOptions.map(
                    (option) => option.value,
                )
                const originalIds = originalOptions.map(
                    (option) => option.value,
                )
                const additions = _.difference(selectedIds, originalIds)
                const deletions = _.difference(originalIds, selectedIds)
                if (additions.length > 0 || deletions.length > 0) {
                    onEdit(id, additions, deletions)
                }
                table.setEditingCell(null)
            }

            return (
                <SearchableSelect
                    autoFocus
                    multiple
                    value={selectedOptions}
                    onChange={(_, value) => setSelectedOptions(value)}
                    options={options}
                    onBlur={onBlur}
                    TextFieldProps={{
                        label: fieldName,
                        required: true,
                        placeholder:
                            selectedOptions.length === 0
                                ? `Select ${fieldName}`
                                : '',
                    }}
                    {...SearchableSelectProps}
                />
            )
        }

        returnedOptions.Edit = Edit
        returnedOptions.enableEditing = true
    } else {
        returnedOptions.enableEditing = false
    }

    if (getOptions && optionsData) {
        const options = getOptions(optionsData)
        const extractFilterValueIds = (values: SearchableSelectOption[]) =>
            values.length > 0 ? values.map((v) => v.value) : undefined
        const Filter: MRT_ColumnDef<RowData>['Filter'] = ({
            header,
            table,
        }) => {
            const [values, setValues] = useState<SearchableSelectOption[]>([])
            useEffectOnce(() => {
                table.getState().columnFilters.forEach((filter) => {
                    if (filter.id === header.column.id && getOptions) {
                        setValues(getOptions(filter.value as ColumnData[]))
                    }
                })
            })

            return (
                <SearchableSelect
                    multiple
                    defaultValue={values}
                    options={options}
                    TextFieldProps={{ placeholder: `Filter by ${fieldName}` }}
                    onChange={(_, values) =>
                        header.column.setFilterValue(
                            extractFilterValueIds(values),
                        )
                    }
                    {...SearchableSelectProps}
                />
            )
        }

        returnedOptions.Filter = Filter
        returnedOptions.enableColumnFilter = true
    } else {
        returnedOptions.enableColumnFilter = false
    }

    return returnedOptions
}

export interface SingleValueColumnDefinitionProps<_RowData, ColumnData> {
    fieldName: string
    optionsData?: ColumnData[]
    renderChip?: (entry: ColumnData | null) => JSX.Element
    getOption?: (data: ColumnData) => SearchableSelectOption
    onEdit?: (id: string, newValue: string) => void
    SearchableSelectProps?: Partial<
        SearchableSelectProps<SearchableSelectOption, false>
    >
}

export const useSingleValueColumnDef = <
    RowData extends MRT_RowData,
    ColumnData,
>({
    fieldName,
    optionsData,
    renderChip,
    getOption,
    onEdit,
    SearchableSelectProps,
}: SingleValueColumnDefinitionProps<RowData, ColumnData>) => {
    const returnedOptions: Partial<MRT_ColumnDef<RowData>> = {}

    if (renderChip) {
        const Cell: MRT_ColumnDef<RowData>['Cell'] = ({ cell }) => {
            const entry = cell.getValue<ColumnData>()
            return <>{renderChip(entry)}</>
        }

        returnedOptions.Cell = Cell
    }

    if (getOption && optionsData && onEdit) {
        const options = optionsData.map(getOption)
        const Edit: MRT_ColumnDef<RowData>['Edit'] = ({ cell, table }) => {
            const id = cell.row.getValue<string>('id')
            const originalValue = cell.getValue<ColumnData | null>()

            const originalOption = originalValue
                ? getOption(originalValue)
                : undefined

            const [selectedOption, setSelectedOption] = useState<
                SearchableSelectOption | undefined
            >(originalOption)
            const onBlur = () => {
                if (selectedOption) {
                    onEdit(id, selectedOption.value)
                }
                table.setEditingCell(null)
            }

            return (
                <SearchableSelect
                    autoFocus
                    value={selectedOption}
                    defaultValue={originalOption}
                    onChange={(_, value) => setSelectedOption(value)}
                    options={options}
                    onBlur={onBlur}
                    TextFieldProps={{
                        label: fieldName,
                        required: true,
                        placeholder:
                            selectedOption === null
                                ? `Select ${fieldName}`
                                : '',
                    }}
                    {...SearchableSelectProps}
                />
            )
        }

        returnedOptions.Edit = Edit
        returnedOptions.enableEditing = true
    }

    return returnedOptions
}
